
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import useTranslation from "next-translate/useTranslation";
import { getInitialStateFromQuery, queryParamsToApiInputVariables, SearchProvider } from "../../components/search/SearchContext";
import Layout from "../../components/Layout";
import Head from "next/head";
import SearchPage from "../../components/search/SearchPage";
import { CountryServiceType, Dog, SearchResult } from "../../interfaces";
import ErrorPage from "next/error";
import { Box, Flex } from "rebass/styled-components";
import serviceRoutes from "../../stories/fixtures/serviceRoutes.json";
import OtherCities from "../../components/sitterprofile/OtherCities";
import BreadcrumbList from "../../components/common/BreadcrumbList";
import { MetaContext } from "../../components/meta";
import HowItWorks from "../../components/sitterprofile/HowItWorks";
import { Benefits } from "../../components/sitterprofile/Contact";
import canonical from "../../lib/canonical";
import { useContext, useState, ReactElement } from "react";
import { SearchServiceTypeDocument, useSearchServiceTypeQuery } from "../../generated/graphql";
import { addApolloState, initializeApollo } from "../../lib/apolloClient";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import { trackOwnerSitterSearched } from "../../lib/event-tracking";
import publicRuntimeConfig from "../../lib/public-runtime-config";
import { useIsMobile, withDeviceType } from "../../lib/media";
interface Props {
    query: Record<string, any>;
    statusCode: number;
    deviceType?: string;
}
interface SearchProps {
    query: Record<string, any>;
    countryServiceTypes: CountryServiceType[];
    userDogs: Dog[];
    searchResults: SearchResult;
    loading: boolean;
    error: any;
    fetchMore: any;
    h1Heading: string;
    h2Heading: string;
    mobileMapVisible: boolean;
    breadcrumb: ReactElement;
    setMobileMapVisible: React.Dispatch<React.SetStateAction<boolean>>;
    isMobileViewport: boolean;
}
function Search(props: SearchProps) {
    const { query, countryServiceTypes, searchResults } = props;
    const initialSearchContextState = getInitialStateFromQuery({
        ...query,
        serviceTypeId: searchResults.serviceType.id
    }, countryServiceTypes);
    return (<SearchProvider initialStateFromQuery={initialSearchContextState}>
      <SearchPage {...props} isSEOLanding/>
    </SearchProvider>);
}
function Service(props: Props) {
    const { t } = useTranslation("search/service");
    const { url } = useContext(MetaContext);
    const [mobileMapVisible, setMobileMapVisible] = useState(false);
    const { query } = useRouter();
    const { statusCode } = props;
    const isMobileViewport = useIsMobile(props.deviceType);
    const searchInputVariables = queryParamsToApiInputVariables(query);
    const { loading, error, data, fetchMore } = useSearchServiceTypeQuery({
        variables: {
            ...searchInputVariables,
            service: serviceRoutes[query.service],
            includePrice: true
        },
        onCompleted: data => {
            trackOwnerSitterSearched(searchInputVariables, data.searchServiceType, !!data.searchServiceType?.caregivers[0]?.bookingEstimation);
        }
    });
    const countryServiceTypes = data && data.country.countryServiceTypes;
    const userDogs = data && data.currentUser ? data.currentUser.dogs : [];
    const searchResults = data && data.searchServiceType ? data.searchServiceType : null;
    const title = searchResults
        ? t(`titleServiceTypeId${searchResults.serviceType.id}`)
        : "";
    const description = searchResults
        ? t(`descriptionServiceTypeId${searchResults.serviceType.id}`)
        : null;
    if (statusCode == 404) {
        return <ErrorPage statusCode={404}/>;
    }
    const breadcrumb = (<BreadcrumbList breadcrumbs={[
            {
                name: t("gudog"),
                href: "/",
                position: "1"
            }
        ]} activeItem={{
            name: t(`breadcrumbTitleServiceTypeId${searchResults?.serviceType.id}`),
            href: "/search/cityservice",
            position: "3"
        }}/>);
    const hrefLangAlternate = publicRuntimeConfig.hrefLangAlternate as any;
    return (<Layout title={title} description={description} displayFooterAreas>
      <Head>
        <link rel="canonical" href={canonical(url)}/>

        {hrefLangAlternate.map(({ href, locale, country, target }, index) => {
            const serviceRoutesForCurrentCountry = process.env.serviceRoutes &&
                process.env.serviceRoutes[country || "en"];
            const route = serviceRoutesForCurrentCountry &&
                searchResults?.serviceType?.routeServiceParam &&
                serviceRoutesForCurrentCountry[searchResults?.serviceType?.routeServiceParam as any];
            return (<link key={index} rel="alternate" href={`${href}${route}`} hrefLang={target}/>);
        })}
      </Head>
      {searchResults && searchResults.serviceType && (<Box>
          <Search countryServiceTypes={countryServiceTypes} userDogs={userDogs} searchResults={searchResults} loading={loading} error={error} fetchMore={fetchMore} h1Heading={t(`h1HeadingServiceTypeId${searchResults.serviceType.id}`)} h2Heading={t(`h2HeadingServiceTypeId${searchResults.serviceType.id}`, {
                sittersCount: searchResults?.totalEntries || "..."
            })} breadcrumb={!mobileMapVisible && breadcrumb} mobileMapVisible={mobileMapVisible} setMobileMapVisible={setMobileMapVisible} isMobileViewport={isMobileViewport} {...props}/>
          {!mobileMapVisible && (<>
              <Box variant="layout.large" pb={5}>
                <Flex mt={[1, 7]} pt={[3, 6]} pb={[1, 2]} px={[1, 0]} sx={{
                    borderTop: "default"
                }} flexDirection={["column", "row"]}>
                  <Box width={["100%", "50%"]} mr={[0, 8]} mb={[3, 0]}>
                    <HowItWorks />
                  </Box>

                  <Box width={["100%", "50%"]}>
                    <Benefits />
                  </Box>
                </Flex>

                <OtherCities serviceType={searchResults.serviceType} topCities={searchResults.topCities} city={searchResults.city} query={query} showCityServiceDescription/>
              </Box>
            </>)}
        </Box>)}
    </Layout>);
}
const getServerSideProps: GetServerSideProps = withDeviceType()(async (context) => {
    const { query, locale, res } = context;
    res.setHeader("Cache-Control", "public, s-maxage=60, stale-while-revalidate=3600");
    const apolloClient = initializeApollo(locale);
    const result = await apolloClient.query({
        query: SearchServiceTypeDocument,
        variables: {
            ...queryParamsToApiInputVariables(query),
            service: serviceRoutes[query.service],
            includePrice: true
        }
    });
    if (!result.data.searchServiceType) {
        return {
            notFound: true
        };
    }
    return addApolloState(apolloClient, {
        props: {
            locale
        }
    });
});
export default Service;

    async function __Next_Translate__getServerSideProps__1980d144674__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/search/service',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1980d144674__ as getServerSideProps }
  